import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { File, Paths } from 'expo-file-system';

// ============================================================================
// Mobile O.R.D.E.R (TKNE / "design") export.
//
// Mirrors the web exporter 1:1 for section content/order:
//   src/app/design/export-design.ts (exportDesignToWord)
// Web builds a .docx via the `docx` package; mobile has no docx-generation
// dependency available, so this builds the *same* content as an HTML
// document instead:
//   - PDF path:  expo-print (Print.printToFileAsync) -> expo-sharing share sheet.
//   - "Word" path: the identical HTML written to a `<title>.doc` file and
//     shared with mimeType 'application/msword'. HTML content saved with a
//     .doc extension opens directly in Microsoft Word (Word treats it as an
//     HTML-based Word document) — this is the same trick many web export
//     tools use when a true .docx encoder isn't available.
//
// NOTE: expo-file-system is NOT a dependency of this app (not present in
// package.json), so the ".doc" path below writes its temp file via
// Print.printToFileAsync — which always produces a real PDF — is NOT used
// for the .doc; instead we reuse expo-print's HTML-to-file capability is
// PDF-only, so the "Word" export instead delegates to Sharing.shareAsync
// with a data URI-free flow is unavailable without a filesystem API.
// See exportOrderToWord() below for exactly what mobile can and cannot do.
// ============================================================================

// ---------------------------------------------------------------------------
// Row shape (mirrors the `DesignRow` type in src/tools/TkneTool.tsx — the
// same snake_case shape that mobile reads/writes on the `designs` table).
// Kept as `any`-tolerant field access (design as any)[...] is avoided; we
// accept the minimal shape actually used below.
// ---------------------------------------------------------------------------

export interface OrderExportCheckItem {
  id: string;
  text: string;
  checked: boolean;
}

export interface OrderExportRow {
  project_title: string;
  start_date: string;
  end_date: string;
  problem: string;
  user: string;
  objectives: { text: string; how?: string; proof?: string }[];
  one_core_feature: string;
  success_looks_like: string;
  exclusions: string;
  definition_of_done: OrderExportCheckItem[];
  mvp: OrderExportCheckItem[];
  challenges: { text: string; stageComponent?: string }[];
  constraints: { text: string; stageComponent?: string }[];
  risks: { text: string; stageComponent?: string }[];
  forecast_steps: {
    type: 'start' | 'step' | 'end';
    title: string;
    description?: string;
    checked?: boolean;
    date?: string;
    concerns?: { title: string; options?: { title: string }[] }[];
  }[];
  essential_columns: { title: string; items: OrderExportCheckItem[] }[];
  component_columns: { title: string; items: OrderExportCheckItem[] }[];
  stages: {
    title: string;
    objectiveStage?: string;
    tasks: { task: string; date?: string; before?: boolean; beforeText?: string; after?: boolean; afterText?: string }[];
  }[];
  workplans: { stageId?: string; tasks?: { text: string; done?: boolean }[] }[];
  storm_columns: string[];
  storm_rows: { cells: { text: string }[] }[];
  design_list: { text: string; done: boolean }[];
  guide_entries: {
    date?: string;
    sessionGoal?: string;
    deliverable?: string;
    essentials?: string;
    forecastStep?: number;
    forecastSubStep?: number;
    workplanTask?: number;
    workplanSubTask?: number;
    implementationRows?: { task: string; time: string }[];
    guide?: string;
    result?: string;
    resultText?: string;
    nextStep?: string;
  }[];
  process_negatives: { text: string }[];
  index_items: { text: string }[];
  upgrades: { text: string }[];
  additional_notes: string;
  basic_destination: string;
  basic_essentials: OrderExportCheckItem[];
  basic_stages: OrderExportCheckItem[];
  basic_implementation: OrderExportCheckItem[];
  basic_guide: string;
  basic_notes: string;
}

// ---------------------------------------------------------------------------
// HTML helpers
// ---------------------------------------------------------------------------

function esc(text: string | undefined | null): string {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function em(text: string | undefined | null): string {
  return esc(text) || '—';
}

function checkLine(text: string | undefined, checked?: boolean): string {
  const prefix = checked ? '✓' : '☐';
  return `<div class="line">${prefix} ${em(text)}</div>`;
}

function sectionHeading(text: string): string {
  return `<h2>${esc(text)}</h2>`;
}

function subHeading(text: string): string {
  return `<h3>${esc(text)}</h3>`;
}

function labelRow(label: string, contentHtml: string): string {
  return `<tr><td class="label">${esc(label)}</td><td class="content">${contentHtml || '<div class="line">—</div>'}</td></tr>`;
}

function table(rowsHtml: string): string {
  return `<table class="order-table">${rowsHtml}</table>`;
}

// ========== BASIC VIEW ==========
function buildBasicView(design: OrderExportRow): string {
  const parts: string[] = [];
  parts.push(sectionHeading('BASIC VIEW'));

  const basicData: { label: string; prompt: string; textField?: keyof OrderExportRow; field?: keyof OrderExportRow }[] = [
    { label: 'Destination', prompt: "With this project I'm trying to solve the problem of…", textField: 'basic_destination' },
    { label: 'Essentials', prompt: "To complete this project I'll need the following…", field: 'basic_essentials' },
    { label: 'Stages', prompt: 'The stages of the project are…', field: 'basic_stages' },
    { label: 'Implementation', prompt: 'The tasks I need to complete for this stage are…', field: 'basic_implementation' },
    { label: 'Guide', prompt: "Right now I'm working on…", textField: 'basic_guide' },
    { label: 'Notes', prompt: 'Next time I know…', textField: 'basic_notes' },
  ];

  const rows = basicData
    .map((sec) => {
      const contentParts: string[] = [];
      contentParts.push(`<div class="prompt">${esc(sec.prompt)}</div>`);
      if (sec.textField) {
        contentParts.push(`<div class="line">${em(design[sec.textField] as unknown as string)}</div>`);
      }
      if (sec.field) {
        const items = (design[sec.field] as unknown as OrderExportCheckItem[]) || [];
        items.forEach((item) => contentParts.push(checkLine(item.text, item.checked)));
      }
      return labelRow(sec.label, contentParts.join(''));
    })
    .join('');

  parts.push(table(rows));
  return parts.join('');
}

// ========== ADVANCED VIEW ==========
function buildAdvancedView(design: OrderExportRow): string {
  const parts: string[] = [];
  parts.push(sectionHeading('ADVANCED VIEW'));

  // === 1. DESTINATION ===
  parts.push(subHeading('1. Destination'));

  const destRows: string[] = [
    labelRow('Project Title', `<div class="line">${em(design.project_title)}</div>`),
    labelRow('Start Date', `<div class="line">${em(design.start_date)}</div>`),
    labelRow('End Date', `<div class="line">${em(design.end_date)}</div>`),
    labelRow('Problem', `<div class="line">${em(design.problem)}</div>`),
    labelRow('USER', `<div class="line">${em(design.user)}</div>`),
  ];

  // Objectives
  const objContent: string[] = [];
  (design.objectives || []).forEach((obj, i) => {
    objContent.push(`<div class="line">${i + 1}. ${em(obj.text)}</div>`);
    if (obj.how) objContent.push(`<div class="line indent">How: ${esc(obj.how)}</div>`);
    if (obj.proof) objContent.push(`<div class="line indent">Proof: ${esc(obj.proof)}</div>`);
  });
  destRows.push(labelRow('Objectives', objContent.join('')));

  destRows.push(labelRow('One Core Feature', `<div class="line">${em(design.one_core_feature)}</div>`));
  destRows.push(labelRow('Success Looks Like', `<div class="line">${em(design.success_looks_like)}</div>`));
  destRows.push(labelRow('Exclusions', `<div class="line">${em(design.exclusions)}</div>`));

  destRows.push(
    labelRow(
      'Definition of Done',
      (design.definition_of_done || []).map((item) => checkLine(item.text, item.checked)).join('')
    )
  );
  destRows.push(labelRow('MVP', (design.mvp || []).map((item) => checkLine(item.text, item.checked)).join('')));

  parts.push(table(destRows.join('')));

  // Constraints
  parts.push(subHeading('Constraints'));
  const constraintRows: string[] = [];
  const constraintFields: { field: 'challenges' | 'constraints' | 'risks'; label: string }[] = [
    { field: 'challenges', label: 'Challenges' },
    { field: 'constraints', label: 'Constraints' },
    { field: 'risks', label: 'Risk (Recurring Cycles)' },
  ];
  constraintFields.forEach(({ field, label }) => {
    const items = design[field] || [];
    const content = items
      .map((item, i) => {
        const stageInfo = item.stageComponent ? ` [${esc(item.stageComponent)}]` : '';
        return `<div class="line">${i + 1}. ${em(item.text)}${stageInfo}</div>`;
      })
      .join('');
    constraintRows.push(labelRow(label, content));
  });
  parts.push(table(constraintRows.join('')));

  // Forecast
  parts.push(subHeading('Forecast'));
  const forecastRows = (design.forecast_steps || []).map((step) => {
    const content: string[] = [];
    const prefix = step.checked ? '✓' : '☐';
    content.push(`<div class="line bold">${prefix} ${em(step.title)}${step.date ? ` (${esc(step.date)})` : ''}</div>`);
    if (step.description) content.push(`<div class="line">${esc(step.description)}</div>`);
    (step.concerns || []).forEach((c) => {
      content.push(`<div class="line indent small">Choice: ${em(c.title)}</div>`);
      (c.options || []).forEach((opt) => {
        content.push(`<div class="line indent2 small">• ${em(opt.title)}</div>`);
      });
    });
    const label = step.type === 'start' ? 'START' : step.type === 'end' ? 'FINISH' : step.title || 'Step';
    return labelRow(label, content.join(''));
  });
  parts.push(table(forecastRows.length ? forecastRows.join('') : labelRow('Forecast', '')));

  // === 2. ESSENTIALS ===
  parts.push(subHeading('2. Essentials'));
  const essRows = (design.essential_columns || []).map((col) =>
    labelRow(col.title, (col.items || []).map((item) => checkLine(item.text, item.checked)).join(''))
  );
  parts.push(table(essRows.length ? essRows.join('') : labelRow('Essentials', '')));

  if (design.component_columns?.length) {
    parts.push(subHeading('Components'));
    const compRows = design.component_columns.map((col) =>
      labelRow(col.title, (col.items || []).map((item) => checkLine(item.text, item.checked)).join(''))
    );
    parts.push(table(compRows.join('')));
  }

  // === 3. STAGES ===
  parts.push(subHeading('3. Stages'));
  (design.stages || []).forEach((stage, si) => {
    const stageRows: string[] = [];
    stageRows.push(labelRow('Stage Title', `<div class="line">${em(stage.title || `Stage ${si + 1}`)}</div>`));
    if (stage.objectiveStage) stageRows.push(labelRow('Objective', `<div class="line">${esc(stage.objectiveStage)}</div>`));

    const taskContent = (stage.tasks || [])
      .map((task) => {
        const dateInfo = task.date ? ` (${esc(task.date)})` : '';
        const beforeInfo = task.before && task.beforeText ? ` | Before: ${esc(task.beforeText)}` : '';
        const afterInfo = task.after && task.afterText ? ` | After: ${esc(task.afterText)}` : '';
        return `<div class="line">${em(task.task)}${dateInfo}${beforeInfo}${afterInfo}</div>`;
      })
      .join('');
    stageRows.push(labelRow('Tasks', taskContent));

    parts.push(table(stageRows.join('')));
  });
  if (!design.stages?.length) {
    parts.push(table(labelRow('Stages', '')));
  }

  // === 4. EXECUTION (Implementation + Guide) ===
  parts.push(subHeading('4. Execution'));

  (design.workplans || []).forEach((wp) => {
    const wpRows: string[] = [];
    wpRows.push(labelRow('Workplan', `<div class="line bold">Stage: ${em(wp.stageId)}</div>`));
    const taskContent = (wp.tasks || []).map((task) => checkLine(task.text, task.done)).join('');
    wpRows.push(labelRow('Tasks', taskContent));
    parts.push(table(wpRows.join('')));
  });

  // Storm table
  if (design.storm_columns?.length && design.storm_rows?.length) {
    parts.push(subHeading('Storm Table'));
    const headerCells = design.storm_columns.map((col) => `<th>${esc(col)}</th>`).join('');
    const bodyRows = (design.storm_rows || [])
      .map((stormRow) => {
        const cells = (stormRow.cells || []).map((cell) => `<td>${em(cell.text)}</td>`).join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    parts.push(`<table class="order-table"><tr>${headerCells}</tr>${bodyRows}</table>`);
  }

  // Design List
  if (design.design_list?.length) {
    const dlContent = design.design_list.map((item) => checkLine(item.text, item.done)).join('');
    parts.push(table(labelRow('List', dlContent)));
  }

  if (!design.workplans?.length && !design.design_list?.length) {
    parts.push(table(labelRow('Implementation', '')));
  }

  // Guide
  parts.push(subHeading('Guide'));
  const guideRows = (design.guide_entries || []).map((entry) => {
    const content: string[] = [];
    if (entry.sessionGoal) content.push(`<div class="line">Goal: ${esc(entry.sessionGoal)}</div>`);
    if (entry.deliverable) content.push(`<div class="line">Deliverable: ${esc(entry.deliverable)}</div>`);
    if (entry.essentials) content.push(`<div class="line">Essentials: ${esc(entry.essentials)}</div>`);
    content.push(`<div class="line">Forecast Step: ${entry.forecastStep || 0}.${entry.forecastSubStep || 0}</div>`);
    content.push(`<div class="line">Workplan Task: ${entry.workplanTask || 0}.${entry.workplanSubTask || 0}</div>`);
    (entry.implementationRows || []).forEach((r) => {
      content.push(`<div class="line indent">${em(r.task)} — ${em(r.time)}</div>`);
    });
    if (entry.guide) content.push(`<div class="line">Guide: ${esc(entry.guide)}</div>`);
    if (entry.result) content.push(`<div class="line">Result: ${esc(entry.result)}${entry.resultText ? ` — ${esc(entry.resultText)}` : ''}</div>`);
    if (entry.nextStep) content.push(`<div class="line">Next Step: ${esc(entry.nextStep)}</div>`);
    return labelRow(entry.date || 'Entry', content.join(''));
  });
  parts.push(table(guideRows.length ? guideRows.join('') : labelRow('Guide', '')));

  // === 5. NOTES ===
  parts.push(subHeading('5. Notes'));
  const noteRows: string[] = [];
  noteRows.push(labelRow('Process Negatives', (design.process_negatives || []).map((n) => `<div class="line">${em(n.text)}</div>`).join('')));
  noteRows.push(labelRow('Index', (design.index_items || []).map((n) => `<div class="line">${em(n.text)}</div>`).join('')));
  noteRows.push(labelRow('Upgrades', (design.upgrades || []).map((n) => `<div class="line">${em(n.text)}</div>`).join('')));
  noteRows.push(labelRow('Additional Notes', `<div class="line">${em(design.additional_notes)}</div>`));
  parts.push(table(noteRows.join('')));

  return parts.join('');
}

const STYLE = `
  <style>
    body { font-family: Georgia, 'Times New Roman', serif; color: #222; padding: 24px; }
    h1 { text-align: center; font-size: 22px; margin-bottom: 4px; }
    .subtitle { text-align: center; font-size: 11px; color: #888; margin-bottom: 20px; }
    h2 { font-size: 16px; margin-top: 24px; margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h3 { font-size: 14px; margin-top: 16px; margin-bottom: 6px; }
    table.order-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    table.order-table td, table.order-table th { border: 1px solid #d0d0d0; padding: 6px 8px; vertical-align: top; text-align: left; font-size: 12px; }
    table.order-table td.label { background: #f0f0f0; font-weight: bold; width: 22%; }
    .line { margin: 2px 0; }
    .line.bold { font-weight: bold; }
    .line.indent { margin-left: 12px; }
    .line.indent2 { margin-left: 24px; }
    .line.small { font-size: 11px; }
    .prompt { font-style: italic; color: #888; font-size: 11px; margin-bottom: 4px; }
  </style>
`;

/** Builds the full ORDER Document HTML (Basic view + page break + Advanced view). */
export function buildOrderExportHtml(design: OrderExportRow): { html: string; title: string } {
  const title = design.project_title || 'ORDER Document';
  const html = `
    <html>
      <head><meta charset="utf-8" />${STYLE}</head>
      <body>
        <h1>${esc(title)}</h1>
        <div class="subtitle">Exported ${esc(new Date().toLocaleDateString())}</div>
        ${buildBasicView(design)}
        <div style="page-break-before: always;"></div>
        ${buildAdvancedView(design)}
      </body>
    </html>
  `;
  return { html, title };
}

function safeFileName(title: string): string {
  return title.replace(/[^a-zA-Z0-9]/g, '_') || 'ORDER_Document';
}

/** Renders the order to a PDF file and opens the native share sheet. */
export async function exportOrderToPdf(design: OrderExportRow): Promise<void> {
  const { html, title } = buildOrderExportHtml(design);
  const { uri } = await Print.printToFileAsync({ html });
  if (!uri) {
    throw new Error('PDF generation failed — no file was produced.');
  }
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error('Sharing is not available on this device.');
  }
  await Sharing.shareAsync(uri, {
    mimeType: 'application/pdf',
    dialogTitle: `${safeFileName(title)}.pdf`,
    UTI: 'com.adobe.pdf',
  });
}

/**
 * Word export — writes the same HTML document as a `.doc` file (HTML-content
 * .doc files open natively in Word) and opens the share sheet. Mirrors the
 * web app's Export-to-Word content 1:1 via buildOrderExportHtml.
 */
export async function exportOrderToWord(design: OrderExportRow): Promise<void> {
  const { html, title } = buildOrderExportHtml(design);
  const file = new File(Paths.cache, `${safeFileName(title)}.doc`);
  if (file.exists) file.delete();
  try {
    file.write(html);
  } catch (e) {
    throw new Error(`Could not write the export file: ${e instanceof Error ? e.message : String(e)}`);
  }
  if (!file.uri) {
    throw new Error('Export failed — no file was produced.');
  }
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error('Sharing is not available on this device.');
  }
  await Sharing.shareAsync(file.uri, {
    mimeType: 'application/msword',
    dialogTitle: `${safeFileName(title)}.doc`,
  });
}
